import * as Sentry from "@sentry/react";
import React, { createContext, useContext, useState, useEffect } from "react";
import type { ReactNode } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import type { User } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { firebaseAuth } from "../firebase";
import { authApi } from "../api/auth.api";
import { deepgramService } from "../services/DeepgramService";
import {
  useUserProfile,
  useUpdateUserProfile,
  useRefreshUserProfile,
} from "../hooks/queries/useUserProfile";
import { queryKeys } from "../lib/react-query/queryKeys";

// Types - canonical source: types/userTypes.ts
export type { PdfSettings, CustomTemplate, UserProfile } from "../types/userTypes";
import type { UserProfile } from "../types/userTypes";

interface UserContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  profileError: boolean;
  updateUserProfile: (updatedData: Partial<UserProfile>) => Promise<boolean>;
  refreshUserProfile: () => Promise<void>;
  hasCompleteProfile: () => boolean;
  isAuthenticated: boolean;
  fullName: string;
  initials: string;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const LOGIN_EVENT_DEDUP_KEY_PREFIX = "helixa:lastLoginEventLoggedAt:";
const LOGIN_EVENT_DEDUP_WINDOW_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Best-effort dedup so a persisted session resolving in several tabs, or a
 * page reload minutes later, doesn't each count as a separate login event.
 * Shared across tabs via localStorage. Fails open (logs) if storage isn't
 * available (private browsing, etc.) — losing the dedup is harmless, only
 * losing the audit signal isn't.
 */
function shouldLogLoginEvent(uid: string): boolean {
  try {
    const key = LOGIN_EVENT_DEDUP_KEY_PREFIX + uid;
    const lastLoggedAt = Number(window.localStorage.getItem(key) || 0);
    if (Date.now() - lastLoggedAt < LOGIN_EVENT_DEDUP_WINDOW_MS) return false;
    window.localStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const currentUserIdRef = React.useRef<string | null>(null);
  const queryClient = useQueryClient();

  const {
    data: userProfileData,
    isPending: profileLoading,
    isError: profileError,
  } = useUserProfile(!!user);
  const userProfile = userProfileData ?? null;
  const updateUserProfileMutation = useUpdateUserProfile();
  const refreshUserProfileQuery = useRefreshUserProfile();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      firebaseAuth,
      async (firebaseUser) => {
        setLoading(true);

        if (firebaseUser) {
          // If a different user is signing in, clear the previous user's cached profile
          if (currentUserIdRef.current && currentUserIdRef.current !== firebaseUser.uid) {
            queryClient.removeQueries({ queryKey: queryKeys.user.profile() });
          }

          try {
            // Force token refresh to verify the session is still valid
            // Retry once after a short delay (network may not be ready after sleep)
            try {
              await firebaseUser.getIdToken(true);
            } catch (tokenError) {
              console.warn("[Auth] Token refresh failed, retrying in 2s...", tokenError);
              await new Promise((r) => setTimeout(r, 2000));
              await firebaseUser.getIdToken(true);
            }
            currentUserIdRef.current = firebaseUser.uid;
            setUser(firebaseUser);
            Sentry.setUser({ id: firebaseUser.uid, email: firebaseUser.email ?? undefined });
            // Login-visibility audit trail — best-effort, never blocks auth.
            // onAuthStateChanged fires once per tab on every persisted-session
            // page load, not just on a fresh credential-based sign-in, so
            // without this dedup every reload/extra tab burns another slot of
            // logLoginEvent's 30/hour cap on volume that isn't really a login.
            // localStorage (not sessionStorage) so the window is shared across
            // tabs of the same browser, not reset per tab.
            if (shouldLogLoginEvent(firebaseUser.uid)) {
              authApi.logLoginEvent().catch(() => {});
            }
          } catch (error) {
            console.warn("[Auth] Token refresh failed after retry, signing out:", error);
            currentUserIdRef.current = null;
            setUser(null);
            Sentry.setUser(null);
            queryClient.removeQueries({ queryKey: queryKeys.user.profile() });
            await signOut(firebaseAuth).catch(() => {});
          }
        } else {
          currentUserIdRef.current = null;
          setUser(null);
          Sentry.setUser(null);
          queryClient.removeQueries({ queryKey: queryKeys.user.profile() });
          deepgramService.clearTranscript();
        }

        setLoading(false);
      }
    );

    return unsubscribe;
  }, [queryClient]);

  // Function to manually refresh user profile
  const refreshUserProfile = async () => {
    if (user) {
      await refreshUserProfileQuery();
    }
  };

  const updateUserProfile = async (
    updatedData: Partial<UserProfile>
  ): Promise<boolean> => {
    try {
      await updateUserProfileMutation.mutateAsync(updatedData);
      return true;
    } catch (error) {
      console.error("Error updating user profile:", error);
      return false;
    }
  };

  // Enhanced profile check - returns true if profile exists and has required fields
  const hasCompleteProfile = (): boolean => {
    if (!userProfile) return false;

    const requiredFields: (keyof UserProfile)[] = [
      "firstName",
      "lastName",
      "email",
    ];
    return requiredFields.every((field) => {
      const value = userProfile[field];
      return value && typeof value === "string" && value.trim().length > 0;
    });
  };

  // Computed values
  const fullName = userProfile
    ? `${userProfile.firstName || ""} ${userProfile.lastName || ""}`.trim()
    : "";

  const initials = userProfile
    ? `${userProfile.firstName?.[0] || ""}${
        userProfile.lastName?.[0] || ""
      }`.toUpperCase()
    : "";

  const value: UserContextType = {
    user,
    userProfile,
    loading,
    profileLoading,
    profileError,
    updateUserProfile,
    refreshUserProfile,
    hasCompleteProfile,
    isAuthenticated: !!user,
    fullName,
    initials,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
